// src/components/tenants/TenantDetailModal.tsx
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';

const client = generateClient<Schema>();

type TenantStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING';

interface Tenant {
    id: string;
    name: string;
    domain: string;
    adminEmail: string;
    orgUnitPath?: string | null;
    status?: TenantStatus | null;
    customerId?: string | null;
    credentialsSecretArn?: string | null;
    encryptedCredentials?: string | null;
    metadata?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
}

interface TenantDetailModalProps {
    tenant: Tenant;
    onClose: () => void;
}

function StatusBadge({ status }: { status?: string | null }) {
    const styles: Record<string, string> = {
        ACTIVE: 'bg-green-100 text-green-800',
        PENDING: 'bg-yellow-100 text-yellow-800',
        INACTIVE: 'bg-gray-100 text-gray-800',
        SUSPENDED: 'bg-red-100 text-red-800',
    };
    const s = status ?? 'INACTIVE';
    return (
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${styles[s] ?? styles.INACTIVE}`}>
            {s}
        </span>
    );
}

export default function TenantDetailModal({ tenant, onClose }: TenantDetailModalProps) {
    const queryClient = useQueryClient();
    const [verifyStatus, setVerifyStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [verifyMessage, setVerifyMessage] = useState('');
    const [confirmSuspend, setConfirmSuspend] = useState(false);
    const [currentStatus, setCurrentStatus] = useState<TenantStatus | null | undefined>(tenant.status);

    // Derive metadata for display
    let parsedMeta: Record<string, string> | null = null;
    try {
        if (tenant.metadata) parsedMeta = JSON.parse(tenant.metadata);
    } catch { /* ignore */ }

    let serviceAccountEmail: string | null = null;
    let projectId: string | null = null;
    try {
        if (tenant.encryptedCredentials) {
            const creds = JSON.parse(tenant.encryptedCredentials);
            serviceAccountEmail = creds.client_email ?? null;
            projectId = creds.project_id ?? null;
        }
    } catch { /* ignore */ }

    // --- Status toggle mutation ---
    const statusMutation = useMutation({
        mutationFn: async (newStatus: TenantStatus) => {
            const { errors } = await client.models.Tenant.update({ id: tenant.id, status: newStatus });
            if (errors) throw new Error(errors[0].message);
            return newStatus;
        },
        onSuccess: (newStatus) => {
            setCurrentStatus(newStatus);
            setConfirmSuspend(false);
            queryClient.invalidateQueries({ queryKey: ['tenants'] });
        },
    });

    const handleToggleStatus = () => {
        if (currentStatus === 'ACTIVE') {
            // Ask for confirmation before suspending
            setConfirmSuspend(true);
        } else {
            // Restore to ACTIVE directly
            statusMutation.mutate('ACTIVE');
        }
    };

    // --- Re-verify credentials mutation ---
    const handleVerify = async () => {
        setVerifyStatus('loading');
        setVerifyMessage('');
        try {
            // @ts-ignore - custom mutation
            const result = await client.mutations.verifyTenantCredentials({ tenantId: tenant.id });
            const data = typeof result.data === 'string' ? JSON.parse(result.data) : result.data;

            if (data.valid) {
                await client.models.Tenant.update({
                    id: tenant.id,
                    status: 'ACTIVE',
                    customerId: data.customerId,
                });
                setCurrentStatus('ACTIVE');
                setVerifyMessage(`Verification succeeded. Customer ID: ${data.customerId}`);
                setVerifyStatus('success');
                queryClient.invalidateQueries({ queryKey: ['tenants'] });
            } else {
                await client.models.Tenant.update({ id: tenant.id, status: 'SUSPENDED' });
                setCurrentStatus('SUSPENDED');
                setVerifyMessage(data.details || data.error || 'Verification failed.');
                setVerifyStatus('error');
                queryClient.invalidateQueries({ queryKey: ['tenants'] });
            }
        } catch (err: any) {
            setVerifyMessage(err.message || 'Verification request failed.');
            setVerifyStatus('error');
        }
    };

    const canVerify = !!tenant.encryptedCredentials;
    const isStatusLoading = statusMutation.isPending;

    return (
        /* Backdrop */
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
            {/* Panel — stop click propagation so clicks inside don't close */}
            <div
                className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">{tenant.name}</h2>
                        <p className="text-sm text-gray-500">{tenant.domain}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 space-y-5">

                    {/* Status + toggle */}
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Status</p>
                            <StatusBadge status={currentStatus} />
                        </div>
                        <div className="flex items-center gap-2">
                            {confirmSuspend ? (
                                <>
                                    <span className="text-sm text-gray-600">Suspend this tenant?</span>
                                    <button
                                        onClick={() => statusMutation.mutate('SUSPENDED')}
                                        disabled={isStatusLoading}
                                        className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                                    >
                                        {isStatusLoading ? 'Saving...' : 'Confirm'}
                                    </button>
                                    <button
                                        onClick={() => setConfirmSuspend(false)}
                                        disabled={isStatusLoading}
                                        className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={handleToggleStatus}
                                    disabled={isStatusLoading || currentStatus === 'PENDING'}
                                    className={`px-3 py-1.5 text-sm rounded border disabled:opacity-50 ${
                                        currentStatus === 'ACTIVE'
                                            ? 'border-red-300 text-red-700 hover:bg-red-50'
                                            : 'border-green-300 text-green-700 hover:bg-green-50'
                                    }`}
                                    title={currentStatus === 'PENDING' ? 'Cannot toggle a pending tenant' : undefined}
                                >
                                    {currentStatus === 'ACTIVE' ? 'Suspend' : 'Restore to Active'}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Details grid */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                        <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Admin Email</p>
                            <p className="text-gray-900 mt-0.5 break-all">{tenant.adminEmail}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Org Unit Path</p>
                            <p className="text-gray-900 mt-0.5">{tenant.orgUnitPath || '/'}</p>
                        </div>
                        {tenant.customerId && (
                            <div>
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Customer ID</p>
                                <p className="text-gray-900 mt-0.5 font-mono text-xs">{tenant.customerId}</p>
                            </div>
                        )}
                        {projectId && (
                            <div>
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">GCP Project</p>
                                <p className="text-gray-900 mt-0.5 font-mono text-xs">{projectId}</p>
                            </div>
                        )}
                        {serviceAccountEmail && (
                            <div className="col-span-2">
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Service Account</p>
                                <p className="text-gray-900 mt-0.5 font-mono text-xs break-all">{serviceAccountEmail}</p>
                            </div>
                        )}
                        {parsedMeta?.onboardedAt && (
                            <div>
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Onboarded</p>
                                <p className="text-gray-900 mt-0.5">
                                    {new Date(parsedMeta.onboardedAt).toLocaleDateString()}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Credential verification */}
                    <div className="border border-gray-200 rounded-md p-4 space-y-3">
                        <div>
                            <p className="text-sm font-medium text-gray-800">Credential Verification</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Re-test the stored service account credentials against the Google Workspace API.
                                {!canVerify && ' No credentials are stored for this tenant.'}
                            </p>
                        </div>

                        {verifyStatus === 'success' && (
                            <div className="p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                                {verifyMessage}
                            </div>
                        )}
                        {verifyStatus === 'error' && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                                {verifyMessage}
                            </div>
                        )}

                        <button
                            onClick={handleVerify}
                            disabled={!canVerify || verifyStatus === 'loading'}
                            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {verifyStatus === 'loading' ? 'Verifying...' : 'Re-verify Credentials'}
                        </button>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-100"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
